import { createFileRoute, redirect } from '@tanstack/react-router';
import Header from '@/components/Header/Header';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@/components/core/Button';
import {
  IconUserPlus,
  IconDataImport,
  IconExportExcel,
  IconSmartphoneMinus,
  IconSmartphonePlus,
} from '@/icons';
import { ActionButtons } from '@/components/Header/ActionButtons';
import Input from '@/components/core/Input';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { FilterPanel } from '@/components/FilterPanel';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ProfileCreateForm } from '@/components/Travellers/ProfileCreateForm';
import { TravellerImportForm } from '@/components/Travellers/TravellerImportForm';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { DialogWindow } from '@/components/Dialog';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { useAscOrDescIcon } from '@/hooks/useSort.tsx';
import {
  SortDirection,
  SearchFilterParams,
  FilterType,
} from '@/types/filterSearch';
import { createSortConfig } from '@/utils/sort';
import { collator } from '@/utils/collator';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import {
  AllpayUserDTO,
  TravellerDTO,
  TravellerMembershipDTO,
  useGetTravellersPerOrganization,
  useListAllpayUsers,
  useListCardCredits,
  useTravellerGlobalSearch,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading';
import { BulkAllpayAssignForm } from '@/components/Travellers/BulkAllpayAssignForm';
import { BulkAllpayRemoveForm } from '@/components/Travellers/BulkAllpayRemoveForm';
import { TravellerListItem } from '@/components/Travellers/TravellerListItem';
import CheckBox from '@/components/core/Checkbox';
import IconButton from '@/components/core/IconButton';
import { DataNotFound } from '@/components/DataNotFound';
import {
  getTravellerAllpayStatus,
  TRAVELLER_ALLPAY_STATUS,
  type TravellerAllpayStatus,
} from '@/helpers/allpayStatus';
import { toCardSearchQuery } from '@/helpers/cardSerial';
import { cn } from '@/helpers/classnames';
import {
  hasAnyDepartmentAccess,
  hasAnyDepartmentWriteAccess,
} from '@/helpers/departmentPermissions';
import {
  filterTravellersByFacets,
  getTravellerFacetFilterKey,
  pruneSelectedIds,
} from '@/helpers/travellerListSelection';
import { getMatchingTravellerSearchCards } from '@/helpers/travellerSearch';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useBulkAllpayDisabled } from '@/contexts/ConfigContext';
import { toast } from '@/hooks/useToast';
import { useTenantID } from '@/hooks/useTenantID';
import { keepPreviousData } from '@tanstack/react-query';
import { useExportTravellersToExcel } from '@/hooks/useExcelExport';
import { useTravellerListSelectionStore } from '@/stores/travellerListSelection';

interface FormattedTraveller extends TravellerDTO {
  memberships: TravellerMembershipDTO[];
  allpayCredit: number;
  cardCredit: number;
  allpayUsers: AllpayUserDTO[];
  allpayStatus: TravellerAllpayStatus;
}

export const Route = createFileRoute('/$tenantId/_layout/travellers/')({
  component: Travellers,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      department: search.department as string[],
      status: search.status as string[],
      allpayStatus: search.allpayStatus as string[],
      query: search.query as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
    };
  },
  beforeLoad: ({ params }) => {
    if (!hasAnyDepartmentAccess()) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Travellers() {
  const tenantId = useTenantID();
  const {
    query,
    department: filterDepartment,
    status: filterStatus,
    allpayStatus: filterAllpayStatus,
    sort,
    direction,
  } = Route.useSearch();
  const rightPanelRef = useRef<HTMLElement>(null);

  const { data: travellers, isLoading: loading } =
    useGetTravellersPerOrganization();

  const { data: allpayUsers } = useListAllpayUsers();
  const { data: cardCredits } = useListCardCredits();

  const isInSelectedDepartments = useCallback(
    (departmentId: string) =>
      !filterDepartment?.length || filterDepartment.includes(departmentId),
    [filterDepartment],
  );

  const processedCardCredits = useMemo(() => {
    return cardCredits?.reduce(
      (acc, card) => {
        if (
          !card.collection_member_id ||
          !isInSelectedDepartments(card.department_id)
        ) {
          return acc;
        }

        const traveller = travellers?.find((t) =>
          t.memberships.find(
            (m) => m.collection_member_id === card.collection_member_id,
          ),
        );

        if (!traveller) {
          return acc;
        }

        acc[traveller.traveller.id] =
          (acc[traveller.traveller.id] || 0) + card.credit;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [cardCredits, isInSelectedDepartments, travellers]);

  const processedAllpayCredits = useMemo(() => {
    return allpayUsers?.reduce(
      (acc, user) => {
        if (!isInSelectedDepartments(user.department_id)) {
          return acc;
        }

        const traveller = travellers?.find((t) =>
          t.memberships.find(
            (m) =>
              m.collection_member_id === user.collection_member_id &&
              m.collection_type === 'master',
          ),
        );

        if (!traveller) {
          return acc;
        }

        acc[traveller.traveller.id] =
          (acc[traveller.traveller.id] || 0) + user.credit;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [allpayUsers, isInSelectedDepartments, travellers]);

  const allpayUsersByMemberId = useMemo(() => {
    const map = new Map<string, AllpayUserDTO[]>();
    for (const user of allpayUsers ?? []) {
      const existing = map.get(user.collection_member_id);
      if (existing) {
        existing.push(user);
      } else {
        map.set(user.collection_member_id, [user]);
      }
    }
    return map;
  }, [allpayUsers]);

  const formattedTravellers = useMemo((): FormattedTraveller[] => {
    if (!travellers) {
      return [];
    }
    return travellers
      .map((traveller) => {
        const memberships = traveller.memberships.filter(
          (m) => m.collection_type === 'master',
        );
        const allpayUsersForTraveller = memberships.flatMap(
          (membership) =>
            allpayUsersByMemberId.get(membership.collection_member_id) ?? [],
        );

        return {
          ...traveller.traveller,
          memberships,
          allpayCredit: processedAllpayCredits?.[traveller.traveller.id] ?? 0,
          cardCredit: processedCardCredits?.[traveller.traveller.id] ?? 0,
          allpayUsers: allpayUsersForTraveller,
          allpayStatus: getTravellerAllpayStatus({
            phone: traveller.traveller.phone,
            hasAllpay: allpayUsersForTraveller.length > 0,
            isActive: memberships.some((membership) => membership.active),
          }),
        };
      })
      .filter((t) => t.memberships.length > 0);
  }, [
    allpayUsersByMemberId,
    processedAllpayCredits,
    processedCardCredits,
    travellers,
  ]);

  const sortConfigs = useMemo(() => {
    const baseConfig = createSortConfig<FormattedTraveller>({
      textFields: ['allpayCredit', 'cardCredit'],
    });

    // Add custom sorting for first_name and last_name with email as secondary sort
    return {
      ...baseConfig,
      first_name: (a: FormattedTraveller, b: FormattedTraveller) => {
        const primarySort = collator.compare(
          a.first_name || '',
          b.first_name || '',
        );
        if (primarySort !== 0) return primarySort;
        return collator.compare(a.email || '', b.email || '');
      },
      last_name: (a: FormattedTraveller, b: FormattedTraveller) => {
        const primarySort = collator.compare(
          a.last_name || '',
          b.last_name || '',
        );
        if (primarySort !== 0) return primarySort;
        return collator.compare(a.email || '', b.email || '');
      },
    };
  }, []);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<FormattedTraveller>({
    items: formattedTravellers || [],
    searchKeys: [
      'first_name',
      'last_name',
      'email',
      'bobcat_private_email',
      'keywords',
    ],
    sortConfigs,
    routePath: Route.fullPath,
    defaultSearchParams: {
      department: [],
      sort: 'first_name',
      direction: 'asc',
    },
  });

  const { formatMessage } = useIntl();
  const getIcon = useAscOrDescIcon();
  const exportTravellersToExcel = useExportTravellersToExcel();
  const bulkAllpayDisabled = useBulkAllpayDisabled();

  const cardSearchQuery = toCardSearchQuery(filterSearch?.query ?? '');
  const cardSearchEnabled = cardSearchQuery !== undefined;

  const { data: cardSearchResults } = useTravellerGlobalSearch(
    { q: cardSearchQuery ?? '' },
    {
      query: {
        enabled: cardSearchEnabled,
        placeholderData: keepPreviousData,
      },
    },
  );

  const cardMatchedTravellers = useMemo((): FormattedTraveller[] => {
    if (!cardSearchQuery || !cardSearchResults?.length) {
      return [];
    }

    const travellerById = new Map(
      formattedTravellers.map((traveller) => [traveller.id, traveller]),
    );

    const matches: FormattedTraveller[] = [];
    const seen = new Set<string>();

    for (const result of cardSearchResults) {
      const matchesCard =
        getMatchingTravellerSearchCards(result, cardSearchQuery).length > 0;
      if (!matchesCard) {
        continue;
      }

      const traveller = travellerById.get(result.traveller.id);
      if (!traveller || seen.has(traveller.id)) {
        continue;
      }

      seen.add(traveller.id);
      matches.push(traveller);
    }

    return matches;
  }, [cardSearchQuery, cardSearchResults, formattedTravellers]);

  const [addTravellerOpen, setAddTravellerOpen] = useState<boolean>(false);
  const [importTravellerOpen, setImportTravellerOpen] =
    useState<boolean>(false);
  const [importTravellerProcessing, setImportTravellerProcessing] =
    useState(false);
  const [announcement, setAnnouncement] = useState('');
  const selectedIdList = useTravellerListSelectionStore(
    (state) => state.selectedIdsByTenant[tenantId],
  );
  const setSelectedIdList = useTravellerListSelectionStore(
    (state) => state.setSelectedIds,
  );
  const selectedIds = useMemo(
    () => new Set(selectedIdList ?? []),
    [selectedIdList],
  );
  const [bulkAllpayOpen, setBulkAllpayOpen] = useState(false);
  const [bulkAllpayProcessing, setBulkAllpayProcessing] = useState(false);
  const [bulkAllpayRemoveOpen, setBulkAllpayRemoveOpen] = useState(false);
  const [bulkAllpayRemoveProcessing, setBulkAllpayRemoveProcessing] =
    useState(false);

  const canBulkAssign =
    !bulkAllpayDisabled &&
    (checkUserPermissions({
      permission: 'organization:editor',
    }) ||
      hasAnyDepartmentWriteAccess());

  const departmentOptions = useMemo(() => {
    if (!travellers?.length) {
      return [];
    }

    return Array.from(
      new Map(
        travellers.flatMap((traveller) =>
          traveller.memberships
            .filter((m) => m.collection_type === 'master')
            .map((membership) => [
              membership.department_id,
              {
                id: membership.department_id,
                label: membership.department_name,
              },
            ]),
        ),
      ).values(),
    );
  }, [travellers]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 'active',
        label: formatMessage({ defaultMessage: 'Aktiv' }),
      },
      {
        id: 'will-inactivate',
        label: formatMessage({ defaultMessage: 'Inaktivering schemalagd' }),
      },
      {
        id: 'pending-inactivation',
        label: formatMessage({
          defaultMessage: 'Inaktivering schemalagd',
        }),
      },
      {
        id: 'inactive',
        label: formatMessage({ defaultMessage: 'Inaktiv' }),
      },
    ];
  }, [formatMessage]);

  const allpayStatusOptions = useMemo(() => {
    return [
      {
        id: TRAVELLER_ALLPAY_STATUS.canHave,
        label: formatMessage({ defaultMessage: 'Kan tilldelas' }),
      },
      {
        id: TRAVELLER_ALLPAY_STATUS.missingPhone,
        label: formatMessage({ defaultMessage: 'Saknar giltigt mobilnummer' }),
      },
      {
        id: TRAVELLER_ALLPAY_STATUS.hasAllpay,
        label: formatMessage({ defaultMessage: 'Har tjänsteresa i appen' }),
      },
    ];
  }, [formatMessage]);

  const filteredTravellers = useMemo(() => {
    const localResults = filterTravellersByFacets(sortedItems, filterSearch);

    if (cardMatchedTravellers.length === 0) {
      return localResults;
    }

    const existingIds = new Set(localResults.map((traveller) => traveller.id));
    const extraResults = filterTravellersByFacets(
      cardMatchedTravellers,
      filterSearch,
    ).filter((traveller) => !existingIds.has(traveller.id));

    if (extraResults.length === 0) {
      return localResults;
    }

    const combined = [...localResults, ...extraResults];
    const sortFn = sortConfigs[filterSearch?.sort as keyof typeof sortConfigs];

    if (!sortFn) {
      return combined;
    }

    return combined.sort((a, b) =>
      filterSearch?.direction === 'desc' ? -1 * sortFn(a, b) : sortFn(a, b),
    );
  }, [sortedItems, cardMatchedTravellers, filterSearch, sortConfigs]);

  const handleFilter = useCallback(
    (
      filter: OptionsType[],
      filterType: 'department' | 'status' | 'allpayStatus',
    ) => {
      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: filter as FilterType[],
      }));

      handleSearchParams({
        [filterType]: filter.map((f) => f.id),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const handleExportToExcel = useCallback(() => {
    if (!filteredTravellers?.length) {
      return;
    }

    try {
      exportTravellersToExcel(filteredTravellers);
    } catch (error) {
      console.error('Failed to export travellers:', error);
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Ett fel uppstod vid export av resenärer',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [filteredTravellers, exportTravellersToExcel, formatMessage]);

  const handleToggleSelected = useCallback(
    (id: string, selected: boolean) => {
      const next = new Set(
        useTravellerListSelectionStore.getState().selectedIdsByTenant[
          tenantId
        ] ?? [],
      );
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      setSelectedIdList(tenantId, [...next]);
    },
    [setSelectedIdList, tenantId],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIdList(
      tenantId,
      filteredTravellers.map((traveller) => traveller.id),
    );
  }, [filteredTravellers, setSelectedIdList, tenantId]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIdList(tenantId, []);
  }, [setSelectedIdList, tenantId]);

  const selectedTravellers = useMemo(
    () =>
      formattedTravellers.filter((traveller) => selectedIds.has(traveller.id)),
    [formattedTravellers, selectedIds],
  );

  const facetFilterKey = useMemo(
    () => getTravellerFacetFilterKey(filterSearch),
    [filterSearch],
  );
  const previousFacetFilterKeyRef = useRef<string | undefined>(undefined);
  const facetFilteredTravellers = useMemo(
    () => filterTravellersByFacets(formattedTravellers, filterSearch),
    [filterSearch, formattedTravellers],
  );

  // Drop ids that disappeared from the loaded list (deleted travellers, lost access).
  useEffect(() => {
    if (!travellers || selectedIds.size === 0) {
      return;
    }

    const next = pruneSelectedIds(
      selectedIds,
      formattedTravellers.map((traveller) => traveller.id),
    );

    if (next.length !== selectedIds.size) {
      setSelectedIdList(tenantId, next);
    }
  }, [
    formattedTravellers,
    selectedIds,
    setSelectedIdList,
    tenantId,
    travellers,
  ]);

  // Keep selection across search and when returning to the page, but drop
  // travellers that no longer match department/status/allpay filters. Prune
  // against the facet-only list so a text query does not shrink the
  // selection when those filters change.
  useEffect(() => {
    const previousKey = previousFacetFilterKeyRef.current;
    previousFacetFilterKeyRef.current = facetFilterKey;

    if (previousKey === undefined || previousKey === facetFilterKey) {
      return;
    }

    if (selectedIds.size === 0) {
      return;
    }

    const next = pruneSelectedIds(
      selectedIds,
      facetFilteredTravellers.map((traveller) => traveller.id),
    );

    if (next.length !== selectedIds.size) {
      setSelectedIdList(tenantId, next);
    }
  }, [
    facetFilterKey,
    facetFilteredTravellers,
    selectedIds,
    setSelectedIdList,
    tenantId,
  ]);

  const allFilteredSelected =
    filteredTravellers.length > 0 &&
    filteredTravellers.every((traveller) => selectedIds.has(traveller.id));
  const someFilteredSelected =
    !allFilteredSelected &&
    filteredTravellers.some((traveller) => selectedIds.has(traveller.id));

  const renderTravellerItem = useCallback(
    (traveller: FormattedTraveller) => {
      const item = (
        <TravellerListItem
          traveller={{ traveller, memberships: traveller.memberships }}
          allpayCredit={traveller.allpayCredit}
          cardCredit={traveller.cardCredit}
        />
      );

      if (!canBulkAssign) {
        return item;
      }

      const displayName =
        [traveller.first_name, traveller.last_name].filter(Boolean).join(' ') ||
        formatMessage({ defaultMessage: 'Anonym resenär' });

      return (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">{item}</div>
          <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
            <CheckBox
              id={`select-traveller-${traveller.id}`}
              checked={selectedIds.has(traveller.id)}
              onCheckedChange={(checked) => {
                handleToggleSelected(traveller.id, checked === true);
              }}
              aria-label={formatMessage(
                { defaultMessage: 'Markera {name}' },
                { name: displayName },
              )}
            />
          </div>
        </div>
      );
    },
    [canBulkAssign, formatMessage, handleToggleSelected, selectedIds],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredTravellers.length;
    const sortLabel =
      filterSearch.sort === 'first_name'
        ? formatMessage({ defaultMessage: 'förnamn' })
        : filterSearch.sort === 'last_name'
          ? formatMessage({ defaultMessage: 'efternamn' })
          : formatMessage({ defaultMessage: 'e-post' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {resenär} other {resenärer}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredTravellers.length, formatMessage]);

  useEffect(() => {
    if (filterSearch) return;

    const departmentIds = filterDepartment || [];
    const statusIds = filterStatus || [];
    const allpayStatusIds = filterAllpayStatus || [];

    const departments = departmentIds
      .map((id) => departmentOptions.find((d) => d.id === id))
      .filter(Boolean) as FilterType[];

    const statuses = statusIds
      .map((id) => statusOptions.find((s) => s.id === id))
      .filter(Boolean) as FilterType[];

    const allpayStatuses = allpayStatusIds
      .map((id) => allpayStatusOptions.find((s) => s.id === id))
      .filter(Boolean) as FilterType[];

    setFilterSearch({
      query: query ?? '',
      department: departments,
      status: statuses,
      allpayStatus: allpayStatuses,
      sort: sort ?? 'first_name',
      direction: direction ?? 'asc',
    });
  }, [
    allpayStatusOptions,
    departmentOptions,
    statusOptions,
    direction,
    filterAllpayStatus,
    filterDepartment,
    filterStatus,
    query,
    sort,
    filterSearch,
    setFilterSearch,
  ]);

  if (loading) {
    return <Loading />;
  }

  if (!travellers) {
    return (
      <DataNotFound
        description={formatMessage({
          defaultMessage: 'Resenärerna kunde inte hämtas',
        })}
      />
    );
  }

  return (
    <>
      <DocumentHead
        titleKey="page.travellers.title"
        descriptionKey="page.travellers.description"
      />
      <Header
        title={formatMessage({ defaultMessage: 'Resenärer' })}
        actions={
          (checkUserPermissions({
            permission: 'organization:editor',
          }) ||
            hasAnyDepartmentWriteAccess()) && (
            <ActionButtons>
              <TravellerActionButtons
                setOpen={setAddTravellerOpen}
                setImportOpen={setImportTravellerOpen}
              />
            </ActionButtons>
          )
        }
      />
      <FilteredListContainer>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel ref={rightPanelRef}>
          {filterSearch && (
            <>
              <FilterPanel
                firstInput={
                  <Input
                    type="text"
                    label={formatMessage({
                      defaultMessage: 'Sök',
                    })}
                    placeholder={formatMessage({
                      defaultMessage:
                        'Sök på namn, e-post, personnummer eller kortnummer',
                    })}
                    id="traveller-search"
                    value={filterSearch.query}
                    onChange={(e) => handleSearch(e.target.value)}
                    aria-description="Sök efter resenärer genom att skriva deras namn, e-post, personnummer eller kortnummer"
                  />
                }
                filterOptions={[
                  <Dropdown
                    label={formatMessage({
                      defaultMessage: 'Avdelning',
                    })}
                    id="department-selection"
                    options={departmentOptions}
                    placeholder={formatMessage({
                      defaultMessage: 'Alla',
                    })}
                    value={filterSearch.department}
                    onValueChange={(value) => handleFilter(value, 'department')}
                    aria-description="Filtrera resenärer genom att välja avdelning"
                    multiSelect
                    containerRef={rightPanelRef}
                  />,
                  <Dropdown
                    label={formatMessage({
                      defaultMessage: 'Status',
                    })}
                    id="status-selection"
                    options={statusOptions}
                    placeholder={formatMessage({
                      defaultMessage: 'Alla',
                    })}
                    value={filterSearch.status}
                    onValueChange={(value) => handleFilter(value, 'status')}
                    aria-description="Filtrera resenärer genom att välja status"
                    multiSelect
                    containerRef={rightPanelRef}
                  />,
                  <Dropdown
                    label={formatMessage({
                      defaultMessage: 'Tjänsteresa i appen',
                    })}
                    id="allpay-status-selection"
                    options={allpayStatusOptions}
                    placeholder={formatMessage({
                      defaultMessage: 'Alla',
                    })}
                    value={filterSearch.allpayStatus}
                    onValueChange={(value) =>
                      handleFilter(value, 'allpayStatus')
                    }
                    aria-description="Filtrera resenärer genom att välja status för tjänsteresa i appen"
                    multiSelect
                    containerRef={rightPanelRef}
                  />,
                ]}
                sortingOptions={[
                  <Button
                    variant={
                      filterSearch.sort === 'first_name' ? 'filled' : 'outline'
                    }
                    startIcon={getIcon({
                      selectedSort: filterSearch.sort!,
                      wantedSort: 'first_name',
                      sortDirection: filterSearch.direction!,
                    })}
                    onClick={() => handleSort('first_name')}
                    aria-label={`Sortera efter förnamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                    aria-pressed={filterSearch.sort === 'first_name'}
                  >
                    <FormattedMessage defaultMessage="Förnamn" />
                  </Button>,
                  <Button
                    variant={
                      filterSearch.sort! === 'last_name' ? 'filled' : 'outline'
                    }
                    startIcon={getIcon({
                      selectedSort: filterSearch.sort!,
                      wantedSort: 'last_name',
                      sortDirection: filterSearch.direction!,
                    })}
                    onClick={() => handleSort('last_name')}
                    aria-label={`Sortera efter efternamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                    aria-pressed={filterSearch.sort === 'last_name'}
                  >
                    <FormattedMessage defaultMessage="Efternamn" />
                  </Button>,
                  <Button
                    variant={
                      filterSearch.sort === 'cardCredit' ? 'filled' : 'outline'
                    }
                    startIcon={getIcon({
                      selectedSort: filterSearch.sort!,
                      wantedSort: 'cardCredit',
                      sortDirection: filterSearch.direction!,
                    })}
                    onClick={() => handleSort('cardCredit', 'desc')}
                    aria-label={`Sortera tjänsteresor på kort ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                    aria-pressed={filterSearch.sort === 'cardCredit'}
                  >
                    <FormattedMessage defaultMessage="Tjänsteresa på kort" />
                  </Button>,
                  <Button
                    variant={
                      filterSearch.sort === 'allpayCredit'
                        ? 'filled'
                        : 'outline'
                    }
                    startIcon={getIcon({
                      selectedSort: filterSearch.sort!,
                      wantedSort: 'allpayCredit',
                      sortDirection: filterSearch.direction!,
                    })}
                    onClick={() => handleSort('allpayCredit', 'desc')}
                    aria-label={`Sortera tjänsteresor i appen ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                    aria-pressed={filterSearch.sort === 'allpayCredit'}
                  >
                    <FormattedMessage defaultMessage="Tjänsteresa i appen" />
                  </Button>,
                ]}
              />
              <Button
                onClick={handleExportToExcel}
                disabled={!filteredTravellers?.length}
                className="mt-2 w-full"
                startIcon={<IconExportExcel />}
              >
                <FormattedMessage defaultMessage="Exportera till Excel" />
              </Button>
            </>
          )}
        </RightPanel>
        <VirtualizedList
          count={filteredTravellers.length}
          countName={formatMessage({ defaultMessage: 'resenärer' })}
          totalItems={formattedTravellers}
          filteredItems={filteredTravellers}
          renderItem={renderTravellerItem}
          getItemKey={(traveller) => traveller.id}
          topRightContent={
            canBulkAssign ? (
              <TravellerListSelectionActions
                allSelected={allFilteredSelected}
                someSelected={someFilteredSelected}
                selectedCount={selectedTravellers.length}
                disabled={filteredTravellers.length === 0}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onOpenBulkAllpay={() => setBulkAllpayOpen(true)}
                onOpenBulkAllpayRemove={() => setBulkAllpayRemoveOpen(true)}
              />
            ) : undefined
          }
        />
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Lägg till resenär' })}
          content={<ProfileCreateForm onOpenChange={setAddTravellerOpen} />}
          onOpenChange={setAddTravellerOpen}
          open={addTravellerOpen}
        />
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Importera resenärer' })}
          content={
            importTravellerOpen ? (
              <TravellerImportForm
                onOpenChange={setImportTravellerOpen}
                onProcessingChange={setImportTravellerProcessing}
              />
            ) : null
          }
          onOpenChange={setImportTravellerOpen}
          open={importTravellerOpen}
          preventClose={importTravellerProcessing}
          className="max-w-5xl"
        />
        <DialogWindow
          title={formatMessage({
            defaultMessage: 'Tilldela tjänsteresa i app',
          })}
          content={
            <BulkAllpayAssignForm
              travellers={selectedTravellers}
              filteredDepartmentIds={
                filterSearch?.department?.map((department) => department.id) ??
                []
              }
              onOpenChange={setBulkAllpayOpen}
              onProcessingChange={setBulkAllpayProcessing}
              onComplete={(failedIds) => setSelectedIdList(tenantId, failedIds)}
            />
          }
          onOpenChange={setBulkAllpayOpen}
          open={bulkAllpayOpen}
          preventClose={bulkAllpayProcessing}
        />
        <DialogWindow
          title={formatMessage({
            defaultMessage: 'Ta bort tjänsteresa i app',
          })}
          description={formatMessage({
            defaultMessage:
              'Bekräfta att du vill ta bort tjänsteresa i app från de markerade resenärerna.',
          })}
          content={
            <BulkAllpayRemoveForm
              travellers={selectedTravellers}
              filteredDepartmentIds={
                filterSearch?.department?.map((department) => department.id) ??
                []
              }
              onOpenChange={setBulkAllpayRemoveOpen}
              onProcessingChange={setBulkAllpayRemoveProcessing}
              onComplete={(failedIds) => setSelectedIdList(tenantId, failedIds)}
            />
          }
          onOpenChange={setBulkAllpayRemoveOpen}
          open={bulkAllpayRemoveOpen}
          preventClose={bulkAllpayRemoveProcessing}
        />
      </FilteredListContainer>
    </>
  );
}

interface TravellerListSelectionActionsProps {
  allSelected: boolean;
  someSelected: boolean;
  selectedCount: number;
  disabled: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onOpenBulkAllpay: () => void;
  onOpenBulkAllpayRemove: () => void;
}

const TravellerListSelectionActions = ({
  allSelected,
  someSelected,
  selectedCount,
  disabled,
  onSelectAll,
  onDeselectAll,
  onOpenBulkAllpay,
  onOpenBulkAllpayRemove,
}: TravellerListSelectionActionsProps) => {
  const { formatMessage } = useIntl();
  const selectAllLabel = allSelected
    ? formatMessage({ defaultMessage: 'Avmarkera alla resenärer' })
    : formatMessage({ defaultMessage: 'Markera alla resenärer' });
  const selectionCountLabel = formatMessage(
    {
      defaultMessage:
        '{count, plural, =0 {Inga resenärer markerade} one {# resenär markerad} other {# resenärer markerade}}',
    },
    { count: selectedCount },
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span
        aria-live="polite"
        aria-atomic="true"
        className={cn(
          'whitespace-nowrap text-sm text-text-700',
          selectedCount === 0 && 'sr-only',
        )}
      >
        {selectionCountLabel}
      </span>
      {selectedCount > 0 && (
        <>
          <IconButton
            aria-label={formatMessage({
              defaultMessage:
                'Tilldela tjänsteresa i app till markerade resenärer',
            })}
            icon={<IconSmartphonePlus aria-hidden="true" />}
            variant="outline"
            onClick={onOpenBulkAllpay}
            title={formatMessage({
              defaultMessage:
                'Tilldela tjänsteresa i app till markerade resenärer',
            })}
          />
          <IconButton
            aria-label={formatMessage({
              defaultMessage:
                'Ta bort tjänsteresa i app från markerade resenärer',
            })}
            icon={<IconSmartphoneMinus aria-hidden="true" />}
            variant="outline"
            onClick={onOpenBulkAllpayRemove}
            title={formatMessage({
              defaultMessage:
                'Ta bort tjänsteresa i app från markerade resenärer',
            })}
          />
        </>
      )}
      <CheckBox
        id="select-all-travellers"
        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
        disabled={disabled}
        onCheckedChange={(checked) => {
          if (checked === true) {
            onSelectAll();
          } else {
            onDeselectAll();
          }
        }}
        aria-label={selectAllLabel}
        toolTip={selectAllLabel}
        className="ml-2"
      />
    </div>
  );
};

interface TravellerActionButtonsProps {
  setOpen: (open: boolean) => void;
  setImportOpen: (open: boolean) => void;
}

const TravellerActionButtons = ({
  setOpen,
  setImportOpen,
}: TravellerActionButtonsProps) => {
  const buttons = [
    {
      icon: <IconUserPlus />,
      label: <FormattedMessage defaultMessage="Lägg till resenär" />,
      onClick: () => setOpen(true),
    },
    {
      icon: <IconDataImport />,
      label: <FormattedMessage defaultMessage="Importera resenärer" />,
      onClick: () => setImportOpen(true),
    },
  ];

  return buttons.map((button, index) => (
    <Button
      id={`${index}-action-button`}
      key={index}
      startIcon={button.icon}
      onClick={button.onClick}
      variant="outline"
    >
      {button.label}
    </Button>
  ));
};
